import {padCharsStart} from 'lodash/fp'

export const numberFormat = (value: number | string): string => {
    // .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
    let formattedNumber = '0'

    if (value) {
        formattedNumber = `${value}`
    }

    const reg = /(^[+-]?\d+)(\d{3})/

    while (reg.test(formattedNumber)) {
        formattedNumber = formattedNumber.replace(reg, '$1,$2')
    }

    return formattedNumber
}

/**
 * 원하는 자리수 이하의 숫자는 그대로 표시 해주고, 이상의 숫자를 00+로 변환해주는 함수
 */
const makeNumberUPtoDigit = (digit: number) => {
    const maxNumber = Math.pow(10, digit) - 1
    return (num: number) => {
        if (isFinite(num)) {
            if (num > maxNumber) {
                return `${maxNumber}+`
            } else if (num >= 0) {
                return `${num}`
            }
        }
        return ''
    }
}

export const formatNumberUpToTwoDigit = makeNumberUPtoDigit(2)

/**
 * length 보다 부족한 자릿수를 값의 왼쪽에 0으로 채워 반환합니다.
 * @param {number | string} value 값
 * @param {number} length 표시할 자릿수
 * @returns {string}
 */
export function zeroPad(value: number | string, length = 2): string {
    return padCharsStart('0')(length)(value.toString())
}

/**
 * 문자를 받아 숫자값만을 뽑습니다.
 * @param number
 * @param defaultValue 값이 없을때 기본이 되는 값
 */
export const toNumberString = (number: string, defaultValue = '') => {
    const reg = /[^0-9]/g
    const result = (number || defaultValue).replace(reg, '')
    return result || defaultValue
}

/**
 * 문자를 받아 숫자로 변환합니다.
 * 변환할 수 없는 값이 들어오면 0으로 반환됩니다.
 */
export const convertStringToNumber = (value?: string): number => {
    return value ? Number(toNumberString(value)) : 0
}

export const makeWonFormat = (digit: number, unit = '원') => {
    const value = Math.pow(10, digit)
    return (number: number) => {
        return `${numberFormat(Math.abs(Number((number / value).toFixed(1))))}${unit}`
    }
}

/**
 * 지정한 자리수의 소수점까지 반올림합니다.
 * 부동소수점 실수를 반환합니다
 * @param value 반올림할 대상 값
 * @param decimalPoint 소수점 자리수
 */
export const makeFloatRound = ({value, decimalPoint}: {value: number; decimalPoint: number}) => {
    return parseFloat(value.toFixed(decimalPoint))
}

export const manWonFormat = makeWonFormat(4, '만원')

/** 사업자 번호 중간에 하이픈 입력 */
export const addHyphenBusinessRegistrationNumber = (businessRegistrationNumber: string) =>
    businessRegistrationNumber?.replace(/(^\d{3})(\d{2})(\d{5})/, '$1-$2-$3')

/** 주어진 인덱스 부터 마스킹 해주는 함수 */
export const maskNumber = (number: string, length: number, from = 0, maskChar = '*') => {
    if (length + from > number.length) {
        throw new Error('length 값을 확인해주세요.')
    }
    return `${number.substring(0, from)}${Array.from({length}, () => maskChar).join('')}${number.substring(
        from + length,
    )}`
}

/** 하이픈 제거 replaceAll은 노드 버전 15이상에서 사용가능하여 test가 실패함 */
export const removeHyphen = (text: string) => text?.replace(/-/g, '') ?? ''

export function decodeHTMLEntity(htmlString: string): string {
    const decodeRules = [
        [`&amp;`, `&`],
        [`&lt;`, `<`],
        [`&gt;`, `>`],
        [`&quot;`, `"`],
        [`&#x27`, `'`],
        [`&#33;`, `!`],
        [`&#34;`, `"`],
        [`&#35;`, `#`],
        [`&#36;`, '$'],
        [`&#37;`, '%'],
        [`&#38;`, `&`],
        [`&#39;`, `'`],
        [`&#61;`, `=`],
    ] as const
    return decodeRules.reduce((decodeHtml, [asIs, toBe]) => decodeHtml.replace(new RegExp(asIs, 'g'), toBe), htmlString)
}
export const getTimeSlot = ({time, isMoreDetail}: {time: number; isMoreDetail?: boolean}) => {
    /**
     * 시간 비교를 위해 변환한 값
     * - 13:00 -> 1300
     * - timeToNumber < 700은 13:00시가 7:00보다 작은지? 의 의미
     */
    if (isMoreDetail) {
        if (time < 700) {
            return '새벽'
        }
        if (time < 1200) {
            return '오전'
        }
        if (time < 1300) {
            return '낮'
        }
        if (time < 1800) {
            return '오후'
        }
        if (time < 2200) {
            return '저녁'
        }
        return '밤'
    } else {
        if (time < 1200) {
            return '오전'
        }
        if (time < 1300) {
            return '낮'
        }
        if (time < 2400) {
            return '오후'
        }
        return '밤'
    }
}

export const getDeliveryTodayMessage = (preparationMinutes: number) => {
    const hour = Math.floor(preparationMinutes / 60)
    const min = preparationMinutes % 60
    return [hour > 0 ? `${hour}시간` : '', min > 0 ? `${min}분` : ''].filter((v) => !!v).join(' ')
}

/**
 * 언어별 금액 표기법으로 숫자를 변환
 */
export const convertToCurrencyString = ({
    amount,
    unitAmount = 10_000,
    unit = '만',
}: {
    amount: number
    unitAmount?: number
    unit?: string
}): string => {
    if (amount % unitAmount === 0) {
        const numberValue = amount / unitAmount

        return `${numberFormat(numberValue)}${unit}`
    }

    return `${numberFormat(amount)}`
}

/**
 * @param replaceNames
 * - [체크, 변경] 형태의 배열
 */
export function makeReplaceName(replaceNames: [string, string][]): (name?: string) => string {
    return (name = ''): string => {
        const targetName = name ?? ''
        for (const [compare, replacePath] of replaceNames) {
            if (new RegExp(compare, 'g').test(targetName)) {
                return targetName.replace(new RegExp(compare, 'g'), replacePath)
            }
        }
        return targetName
    }
}

/** string 형식 true, false를 boolean으로 변환 */
export const convertStringToBoolean = (value: string): boolean => {
    if (value.toLocaleLowerCase() === 'true') {
        return true
    }
    if (value.toLocaleLowerCase() === 'false') {
        return false
    }
    return false
}
